/**
 * Central domain configuration — single source of truth.
 *
 * Auto-detects environment from the browser hostname at runtime so the same
 * codebase works on every deployment target without env-var tweaking:
 *
 *   snaplayer.app  / www.snaplayer.app  → production  (snaplayer.pics)
 *   dev.snaplayer.app                   → development (dev.snaplayer.pics)
 *   beta.snaplayer.app                  → beta        (dev.snaplayer.pics)
 *   localhost / *.vercel.app            → local       (dev.snaplayer.pics)
 *
 * VITE_* env vars are respected as **optional overrides** (useful for local
 * testing against a custom worker).  They are NOT required for production
 * vs dev differentiation — that is handled by hostname detection.
 */

// ── Helpers ─────────────────────────────────────────────────────────

export type SnapEnvironment = 'production' | 'development' | 'beta' | 'local';

function detectEnvironment(): SnapEnvironment {
  if (typeof window === 'undefined') {
    // SSR / non-browser — use Vite build mode as hint
    return import.meta.env.PROD ? 'production' : 'development';
  }
  const h = window.location.hostname;
  if (h === 'snaplayer.app' || h === 'www.snaplayer.app') return 'production';
  if (h === 'dev.snaplayer.app') return 'development';
  if (h === 'beta.snaplayer.app') return 'beta';
  return 'local'; // localhost, 127.0.0.1, *.vercel.app, etc.
}

/** Strip literal "\n" that can leak from Vercel `echo` piping and trailing slashes. */
function cleanEnv(value: string | undefined): string {
  if (!value) return '';
  return value.trim().replace(/\\n/g, '').replace(/\n/g, '').replace(/\/+$/, '');
}

// ── Lazy singleton ──────────────────────────────────────────────────

let _env: SnapEnvironment | null = null;

function env(): SnapEnvironment {
  if (_env === null) _env = detectEnvironment();
  return _env;
}

// ── Domain maps ─────────────────────────────────────────────────────

const SNAP_BASE_MAP: Record<SnapEnvironment, string> = {
  production:  'https://snaplayer.pics',
  development: 'https://dev.snaplayer.pics',
  beta:        'https://beta.snaplayer.pics',
  local:       'https://dev.snaplayer.pics',
};

const APP_BASE_MAP: Record<SnapEnvironment, string> = {
  production:  'https://snaplayer.app',
  development: 'https://dev.snaplayer.app',
  beta:        'https://beta.snaplayer.app',
  local:       'https://dev.snaplayer.app', // safe default; override at callsite
};

// ── Public exports ──────────────────────────────────────────────────

/** Current detected environment. */
export const ENVIRONMENT: SnapEnvironment = /* @__PURE__ */ detectEnvironment();

/** True when serving from the production domain (snaplayer.app). */
export const IS_PRODUCTION: boolean = ENVIRONMENT === 'production';

/** True for any non-production deployment. */
export const IS_DEV: boolean = !IS_PRODUCTION;

/**
 * Cloudflare render-worker base URL.
 * e.g. `https://snaplayer.pics` (prod) or `https://dev.snaplayer.pics` (dev).
 *
 * Respects `VITE_SNAP_BASE` or `localStorage.sl_snap_base_override` as overrides.
 */
export const SNAP_BASE: string = (() => {
  // 1. localStorage override (dev tooling)
  try {
    if (typeof window !== 'undefined') {
      const ls = window.localStorage.getItem('sl_snap_base_override');
      if (ls && /^https?:\/\//i.test(ls)) return ls.replace(/\/+$/, '');
    }
  } catch { /* noop */ }
  // 2. VITE env override
  const fromEnv = cleanEnv(import.meta.env.VITE_SNAP_BASE as string | undefined);
  if (fromEnv.length && fromEnv.startsWith('http')) return fromEnv;
  // 3. Hostname-based default
  return SNAP_BASE_MAP[env()];
})();

/**
 * App origin (Vercel).
 * e.g. `https://snaplayer.app` or `https://dev.snaplayer.app`.
 * On localhost/preview falls back to `window.location.origin`.
 */
export const APP_BASE: string = (() => {
  const e = env();
  if (e === 'local' && typeof window !== 'undefined') return window.location.origin;
  return APP_BASE_MAP[e];
})();

/**
 * R2 user-uploads asset host (u.snaplayer.pics).
 * Shared across all environments (single bucket).
 * Respects `window.SNAP_ASSET_HOST` runtime override set by the editor iframe bridge.
 */
export const ASSET_HOST: string = (() => {
  if (typeof window !== 'undefined') {
    const w = (window as Record<string, unknown>).SNAP_ASSET_HOST;
    if (typeof w === 'string' && w.trim().length) return w.trim().replace(/\/+$/, '');
  }
  return 'https://u.snaplayer.pics';
})();

/**
 * R2 templates bucket host (t.snaplayer.pics).
 * Respects `VITE_R2_TEMPLATES_BASE_URL` override.
 */
export const TEMPLATES_BASE_URL: string = (() => {
  const fromEnv = cleanEnv(import.meta.env.VITE_R2_TEMPLATES_BASE_URL as string | undefined);
  if (fromEnv.length && fromEnv.startsWith('http')) return fromEnv;
  return 'https://t.snaplayer.pics';
})();

/**
 * Render base URL used by the Dashboard for template preview thumbnails.
 * Respects `VITE_RENDER_BASE_URL` override, otherwise falls back to SNAP_BASE.
 */
export const RENDER_BASE_URL: string = (() => {
  const fromEnv = cleanEnv(import.meta.env.VITE_RENDER_BASE_URL as string | undefined);
  if (fromEnv.length && fromEnv.startsWith('http')) return fromEnv;
  return SNAP_BASE;
})();

/** Show dev-only UI: debug panels, preview banners, etc. */
export const SHOW_DEV_UI: boolean = IS_DEV;

/**
 * Check whether a hostname is a non-production host
 * (Vercel preview, dev/beta subdomain, snaplayer.pics render domain, localhost).
 */
export function isPreviewHost(hostname?: string): boolean {
  const h = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  return (
    h.includes('vercel.app') ||
    h === 'dev.snaplayer.app' ||
    h === 'beta.snaplayer.app' ||
    h.endsWith('.snaplayer.pics') ||
    h === 'snaplayer.pics' ||
    h === 'localhost' ||
    h === '127.0.0.1'
  );
}

/**
 * Check if an origin is trusted for postMessage communication.
 * Used by both Editor.tsx (parent) and EditorApp.tsx (iframe child).
 */
export function isTrustedOrigin(origin: string): boolean {
  if (!origin) return false;
  try {
    const u = new URL(origin);
    const h = u.hostname;
    return (
      h === 'snaplayer.app' ||
      h === 'www.snaplayer.app' ||
      h === 'dev.snaplayer.app' ||
      h === 'beta.snaplayer.app' ||
      h.endsWith('.vercel.app') ||
      h === 'localhost' ||
      h === '127.0.0.1'
    );
  } catch {
    return origin === 'null'; // sandboxed iframe
  }
}

/** True when running on a dev/preview host where serviceRole auth is OK. */
export function isDevOrPreview(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h.includes('localhost') ||
    h.includes('vercel.app') ||
    h === 'dev.snaplayer.app' ||
    h === 'beta.snaplayer.app'
  );
}
